import { Box, Skeleton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { CLIP_PATHS, PREVIEW_HEIGHT, PREVIEW_WIDTH } from "./constants";
import { resolveTextCardVariant } from "./frameVariant";
import { EmailCardPreview } from "./previews/EmailCardPreview";
import { TextCardPreview } from "./previews/TextCardPreview";
import type { FrameCardPreviewProps } from "./types";

import { frameVariantPreviewQueryOptions } from "@/api/dragon-tiles";
import { isFramePreviewVariant } from "@/types/dragon-tiles";

/**
 * Preview gallery/router - delegates to specific preview components
 * based on selected medium and variant availability
 */
export function FrameCardPreview({
  frame,
  siteId,
  selectedMedium,
  variantPreviews,
}: FrameCardPreviewProps) {
  const selectedVariant = useMemo(() => {
    if (!selectedMedium) return null;
    const exact = frame.frame_variants.find((v) => v.medium === selectedMedium);
    if (exact) return exact;
    if (selectedMedium === "sms") {
      return resolveTextCardVariant(frame)?.variant ?? null;
    }
    return null;
  }, [selectedMedium, frame]);

  const variantPreview = selectedVariant?.id
    ? variantPreviews?.[selectedVariant.id]
    : undefined;

  const isTextVariant =
    selectedVariant?.medium === "sms" || selectedVariant?.medium === "mms";

  const { data: fetchedTextPreview, isLoading: isTextPreviewLoading } =
    useQuery({
      ...frameVariantPreviewQueryOptions(siteId, selectedVariant?.id ?? ""),
      enabled: Boolean(
        isTextVariant && selectedVariant?.id && variantPreview === undefined,
      ),
    });

  // Render placeholder if no variant exists for selected medium
  if (!selectedVariant) {
    return (
      <Box
        sx={{
          backgroundColor: (theme) =>
            theme.palette.background["paper-elevation-1"],
          clipPath: CLIP_PATHS.top,
          height: PREVIEW_HEIGHT,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box sx={{ width: PREVIEW_WIDTH }} />
      </Box>
    );
  }

  if (isTextVariant) {
    if (variantPreview === undefined && isTextPreviewLoading) {
      return (
        <Box
          sx={{
            backgroundColor: "common.white",
            clipPath: CLIP_PATHS.top,
            height: PREVIEW_HEIGHT,
          }}
        >
          <Skeleton variant="queen" width="100%" height="100%" />
        </Box>
      );
    }
    return (
      <TextCardPreview
        siteId={siteId}
        selectedVariant={selectedVariant}
        initialPreviewContent={
          variantPreview?.textPreviewContent ??
          fetchedTextPreview?.textPreviewContent
        }
      />
    );
  }

  // Default to email preview (handles both "email" and "email-mobile")
  return (
    <EmailCardPreview
      frameName={frame.name}
      siteId={siteId}
      variantId={selectedVariant.id}
      manifest={
        isFramePreviewVariant(selectedVariant)
          ? selectedVariant.manifest
          : undefined
      }
      initialPreview={variantPreview?.body}
    />
  );
}
